import {
  useEffect,
  useCallback,
} from 'react';
import {
  Route,
  Routes,
  useParams,
  useNavigate,
  useLocation,
} from 'react-router';
import {
  getStatus,
} from 'redux-resource';

import {
  shallowEqual,
  useDispatch,
  useSelector,
} from 'react-redux';

import {
  coreActions,
  coreSelectors,
} from '@gymcloud/core';
import {
  uiActions,
} from 'web-actions';
import {
  getUiState,
} from 'web-selectors';
import {
  usePageView,
} from 'web-hooks';

import {
  Icon,
  Button,
  Assign,
  Spinner,
  NotFound,
  Document,
  NavigationTabs,
  ContentWrapper,
} from 'web-components';

import {
  Flex,
  TextBreadcrumbs,
} from 'web-styled';
import isPropValid from '@emotion/is-prop-valid';

const DocumentPage = () => {
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const id = Number(params.id);
  const list = `document_${id}`;
  const mapState = useCallback(state => ({
    readStatus: getStatus(
      state,
      [
        'documents.requests.fetchDocument.status',
        `documents.meta[${id}].readStatus`,
      ],
      true,
    ),
    document: coreSelectors.getResourceById(
      'documents',
      id,
    )(state),
    author: coreSelectors.getDocumentAuthor(
      id,
    )(state),
    profile: coreSelectors.getProfile(state),
    assignees: coreSelectors.getDocumentAssignees(
      list,
    )(state),
    clientsMeta: coreSelectors.getRequestProperties(
      'clients',
      'fetchIndividuals',
    )(state).meta,
    ...getUiState([
      'fileUpload',
      'openedTooltips',
    ])(state),
  }), [id]);
  const {
    author,
    profile,
    document,
    assignees,
    readStatus,
    fileUpload,
    clientsMeta,
    openedTooltips,
  } = useSelector(mapState, shallowEqual);
  const dispatch = useDispatch();
  const isEdit = location.pathname.endsWith('/edit');

  useEffect(() => {
    const markPassedStep = () => {
      const userSteps = profile.user_settings.tutorial_steps;
      if (userSteps.indexOf('review_documents') === -1) {
        dispatch(
          coreActions.requestUpdateSettings({
            body: {
              tutorial_steps: [...userSteps, 'review_documents'],
            },
            settingsId: profile.user_settings.id,
          }),
        );
      }
    };

    dispatch(
      coreActions.requestGetDocument({
        id,
        withAssignees: true,
        callbacks: {
          onSuccess: (doc) => {
            if (doc.name.startsWith('HTS')) {
              markPassedStep();
            }
          },
        },
      }),
    );
  }, [id]);

  usePageView({
    eventKey: 'viewed_document',
    attrs: document,
    enabled: readStatus.succeeded && document,
  });

  if (readStatus.succeeded && document?.id && !profile.is_pro) {
    window.location.href = document.document_file?.url
  }

  const back = () => {
    navigate('/documents');
  };

  const handleDelete = () => {
    const onConfirmAction = coreActions.requestDeleteEntity({
      id,
      resourceType: 'documents',
    });

    dispatch(
      uiActions.confirmModal({
        modal: {
          title: 'Delete document?',
          bodyText: `This will unassign (${document.name}) from all clients and remove it from your list.`,
        },
        onSuccess: back,
        onConfirmAction,
      }),
    );
  };

  const handleDropFile = (files: Array<File>) => {
    dispatch(
      uiActions.requestUploadDocuments({ files, documentId: id }),
    );
  };

  const onView = () => {
    if (openedTooltips.some(el => el.key === 'view_document')) {
      const userSteps = profile.user_settings.tutorial_steps;
      dispatch(
        uiActions.setUiState({
          openedTooltips: openedTooltips.filter(el => el.key !== 'view_document'),
        }),
      );

      dispatch(
        coreActions.requestUpdateSettings({
          body: {
            tutorial_steps: [...new Set(userSteps), 'review_documents'],
          },
          settingsId: profile.user_settings.id,
        }),
      );
    }
  };

  const onSubmit = (payload: any, actions: any) => {
    dispatch(
      coreActions.requestUpdateEntity({
        id,
        payload,
        endNotify: true,
        callbacks: {
          ...actions,
          onSuccess: () => {
            navigate(`/documents/${id}`);
          },
        },
        resourceType: 'documents',
      }),
    );
  };

  const handleAssignClient = (client: any) => {
    dispatch(
      coreActions.requestAssignDocumentToClient({
        id,
        payload: {
          user_id: client.id,
        },
      }),
    );
  };

  const handleUnAssignClient = (assignment: any) => {
    dispatch(
      coreActions.requestUnAssignDocumentToClient({
        id,
        assignmentId: assignment.id,
      }),
    );
  };

  const handleAssignGroup = (group: any) => {
    dispatch(
      coreActions.requestAssignDocumentToGroup({
        id: group.id,
        list,
        payload: {
          document_id: id,
        },
      }),
    );
  };

  const handleUnAssignGroup = (group: any) => {
    const onConfirmAction = coreActions.requestUnAssignDocumentToGroup({
      id: group.id,
      qs: {
        document_id: id,
      },
    });

    dispatch(
      uiActions.confirmModal({
        modal: {
          title: 'Unassign document?',
          bodyText: 'Are you sure you want unassign document from group?',
        },
        onConfirmAction,
      }),
    );
  };

  const tabs = [
    {
      to: `/documents/${id}${isEdit ? '/edit' : ''}`,
      name: 'Document',
    },
    {
      to: `/documents/${id}/assign`,
      name: 'Assign',
    },
  ];

  return (
    <ContentWrapper
      header={(
        <Flex fillSpace align="center" justify="space-between">
          <Flex align="center" spacing={10}>
            <Button
              appearance="link"
              onClick={() => navigate(-1)}
              noPrint
            >
              <Icon type="fa fa-chevron-left" />
            </Button>
            <Flex spacing={15}>
              <TextBreadcrumbs>
                {document?.name}
              </TextBreadcrumbs>
            </Flex>
          </Flex>
          {profile.is_pro && (
            <NavigationTabs
              tabs={tabs}
            />
          )}
        </Flex>
      )}
    >
      {(readStatus.succeeded && profile.is_pro) && (
        <Routes>
          <Route
            index
            element={(
              <Document
                readOnly
                author={author}
                document={document}
                fileUpload={fileUpload}
                openedTooltips={openedTooltips}
                onView={onView}
                onSubmit={onSubmit}
                onDelete={handleDelete}
                onDropFile={handleDropFile}
              />
            )}
          />
          <Route
            path="edit"
            element={(
              <Document
                author={author}
                document={document}
                fileUpload={fileUpload}
                onSubmit={onSubmit}
                onDelete={handleDelete}
                onDropFile={handleDropFile}
              />
            )}
          />
          <Route
            path="assign"
            element={(
              <Assign
                assignees={assignees}
                clientsMeta={clientsMeta}
                onAssignClient={handleAssignClient}
                onUnAssignClient={handleUnAssignClient}
                onAssignGroup={handleAssignGroup}
                onUnAssignGroup={handleUnAssignGroup}
              />
            )}
          />
        </Routes>
      )}

      {readStatus.pending && (
        <Spinner message="Loading Document..." />
      )}

      {readStatus.failed && (
        <NotFound title="Something went wrong" />
      )}
    </ContentWrapper>
  );
};

export default DocumentPage;
